/**
 * Composant DetailsDialog - Dialogue affichant les détails d'un prospect
 * Inclut: informations de contact, formation, notes, historique devis/factures, relances
 */

import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { 
  Mail, Phone, FileText, Download, Eye, Send, Receipt, DollarSign, 
  Trash2, XCircle, CreditCard, CalendarClock, RefreshCw, Clock, Edit2, Pencil
} from 'lucide-react';
import { libelleOrigine } from '../OriginePicker';
import { useState } from 'react';
import { EmailEnvoyeDialog } from '../../emails/EmailEnvoyeDialog';

// Composant pour afficher un payeur avec ses actions
const PayeurCard = ({ 
  payeur, 
  devis, 
  hasMultiplePayeurs, 
  selectedProspect,
  onPreviewDevis,
  onDownloadDevis,
  onSendDevisByEmail,
  onCreateFacture,
  onPreviewFacture,
  onDownloadFacture,
  onSendFactureByEmail,
  onPaiement
}) => {
  const currentDevisId = hasMultiplePayeurs ? payeur.devisId : devis.devis_id;
  const partage = !!devis.devis_partage;
  const payerFacture = selectedProspect.factures?.find(f => 
    (partage && f.devis_id === currentDevisId) || (f.devis_id === devis.devis_id && f.payer_type === payeur.type.toLowerCase())
  ) || (selectedProspect.factures?.find(f => f.devis_id === devis.devis_id) && !hasMultiplePayeurs 
    ? selectedProspect.factures.find(f => f.devis_id === devis.devis_id) 
    : null);

  return (
    <div className={`p-2 rounded border ${payeur.color}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{payeur.type}</span>
          <span className="text-xs opacity-80">({payeur.nom})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold">{payeur.montant.toLocaleString('fr-FR')} €</span>
          {!partage && (<>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 hover:bg-white/50"
            onClick={() => onPreviewDevis(currentDevisId)}
            title={`Voir devis ${payeur.type}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 hover:bg-white/50"
            onClick={() => onDownloadDevis(currentDevisId)}
            title={`Télécharger devis ${payeur.type}`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          </>)}
        </div>
      </div>
      
      <div className="flex flex-wrap gap-1 pt-1 border-t border-current/20">
        {!partage && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-xs px-2 hover:bg-white/50"
          onClick={() => onSendDevisByEmail(currentDevisId)}
          title={`Envoyer devis ${payeur.type} par email`}
        >
          <Send className="h-3 w-3 mr-1" />
          Envoyer devis
        </Button>
        )}
        
        {!payerFacture && !(partage && devis.factures_par_payeur?.[currentDevisId.split('-').pop()]?.generee) && (devis.statut === 'accepte' || devis.statut === 'signe') && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs px-2 hover:bg-white/50 text-blue-600"
            onClick={() => onCreateFacture(currentDevisId)}
            title={`Générer facture ${payeur.type}`}
            data-testid={`create-facture-${currentDevisId}`}
          >
            <Receipt className="h-3 w-3 mr-1" />
            Générer facture
          </Button>
        )}
        
        {payerFacture && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs px-2 hover:bg-white/50 text-green-600"
              onClick={() => onPreviewFacture(payerFacture.facture_id)}
              title={`Voir facture ${payeur.type}`}
            >
              <Eye className="h-3 w-3 mr-1" />
              Voir facture
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs px-2 hover:bg-white/50 text-green-600"
              onClick={() => onDownloadFacture(payerFacture.facture_id)}
              title={`Télécharger facture ${payeur.type}`}
            >
              <Download className="h-3 w-3 mr-1" />
              Facture
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs px-2 hover:bg-white/50 text-blue-600"
              onClick={() => onSendFactureByEmail(payerFacture.facture_id)}
              title={`Envoyer facture ${payeur.type}`}
            >
              <Send className="h-3 w-3 mr-1" />
              Envoyer facture
            </Button>
          </>
        )}
        
        {(devis.statut === 'accepte' || devis.statut === 'signe') && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs px-2 hover:bg-white/50 text-purple-600"
            onClick={() => onPaiement(payeur)}
            title={`Enregistrer paiement ${payeur.type}`}
          >
            <DollarSign className="h-3 w-3 mr-1" />
            Paiement
          </Button>
        )}
      </div>
    </div>
  );
};

// Composant pour afficher un devis dans l'historique
const DevisCard = ({ 
  devis, 
  selectedProspect, 
  onDeleteDevis,
  onPreviewDevis,
  onDownloadDevis,
  onSendDevisByEmail,
  onCreateFacture,
  onPreviewFacture,
  onDownloadFacture,
  onSendFactureByEmail,
  onDownloadAllDevis,
  onRefuseDevis,
  onPaiement,
  onPaiementDevis
}) => {
  // Déterminer les payeurs actifs
  const payeurs = [];
  const baseDevisId = devis.devis_id?.split('-')[0] || devis.devis_id;
  
  // Fonction helper pour calculer le montant d'un payeur (utilise montant_total si montant est 0)
  const getPayeurMontant = (payeurData, isOnlyActivePayeur) => {
    if (payeurData?.montant > 0) return payeurData.montant;
    // Si c'est le seul payeur actif et montant est 0, utiliser montant_total
    if (isOnlyActivePayeur && devis.montant_total > 0) return devis.montant_total;
    return payeurData?.montant || 0;
  };
  
  // Compter les payeurs actifs pour déterminer si on doit utiliser montant_total
  const activePayeursCount = [
    devis.facturation?.stagiaire?.actif,
    devis.facturation?.entreprise?.actif,
    devis.facturation?.financeur?.actif
  ].filter(Boolean).length;
  
  const isOnlyOnePayer = activePayeursCount === 1;
  
  if (devis.facturation?.stagiaire?.actif) {
    const montant = getPayeurMontant(devis.facturation.stagiaire, isOnlyOnePayer);
    if (montant > 0) {
      payeurs.push({
        type: 'Stagiaire',
        nom: `${selectedProspect.prenom} ${selectedProspect.nom}`,
        montant: montant,
        color: 'bg-green-100 text-green-700 border-green-300',
        devisId: `${baseDevisId}-STG`
      });
    }
  }
  if (devis.facturation?.entreprise?.actif) {
    const montant = getPayeurMontant(devis.facturation.entreprise, isOnlyOnePayer);
    if (montant > 0) {
      payeurs.push({
        type: 'Entreprise',
        nom: devis.facturation.entreprise.nom || 'Non spécifié',
        montant: montant,
        color: 'bg-blue-100 text-blue-700 border-blue-300',
        devisId: `${baseDevisId}-ENT`
      });
    }
  }
  if (devis.facturation?.financeur?.actif) {
    const montant = getPayeurMontant(devis.facturation.financeur, isOnlyOnePayer);
    if (montant > 0) {
      payeurs.push({
        type: 'Financeur',
        nom: devis.facturation.financeur.nom || 'Non spécifié',
        montant: montant,
        color: 'bg-purple-100 text-purple-700 border-purple-300',
        devisId: `${baseDevisId}-FIN`
      });
    }
  }
  // Legacy payer
  if (devis.payer && payeurs.length === 0) {
    payeurs.push({
      type: devis.payer.type === 'stagiaire' ? 'Stagiaire' : devis.payer.type === 'entreprise' ? 'Entreprise' : 'Financeur',
      nom: devis.payer.nom,
      montant: devis.montant_payeur || devis.montant_total,
      color: devis.payer.type === 'stagiaire' ? 'bg-green-100 text-green-700 border-green-300' : 
             devis.payer.type === 'entreprise' ? 'bg-blue-100 text-blue-700 border-blue-300' : 
             'bg-purple-100 text-purple-700 border-purple-300',
      devisId: devis.devis_id
    });
  }
  
  // Fallback final : si aucun payeur détecté mais devis a un montant total, créer un payeur par défaut
  if (payeurs.length === 0 && devis.montant_total > 0) {
    payeurs.push({
      type: 'Stagiaire',
      nom: `${selectedProspect.prenom} ${selectedProspect.nom}`,
      montant: devis.montant_total,
      color: 'bg-green-100 text-green-700 border-green-300',
      devisId: devis.devis_id
    });
  }
  
  const hasMultiplePayeurs = payeurs.length > 1;
  const partage = !!devis.devis_partage;

  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs bg-slate-200 px-2 py-1 rounded">
            {devis.devis_id?.slice(0, 8)}...
          </span>
          <span className="text-xs text-slate-500">
            {new Date(devis.date_creation).toLocaleDateString('fr-FR')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={
            devis.statut === 'accepte' || devis.statut === 'signe' ? 'bg-green-500' :
            devis.statut === 'envoye' ? 'bg-blue-500' :
            devis.statut === 'refuse' ? 'bg-red-500' : 'bg-slate-400'
          }>
            {devis.statut === 'accepte' ? 'Accepté' :
             devis.statut === 'signe' ? 'Signé' :
             devis.statut === 'envoye' ? 'Envoyé' :
             devis.statut === 'refuse' ? 'Refusé' : 'Brouillon'}
          </Badge>
          {partage && <Badge className="bg-indigo-500" data-testid={`badge-devis-partage-${devis.devis_id}`}>Partagé</Badge>}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDeleteDevis(devis.devis_id, devis.statut)}
            title="Supprimer le devis"
            data-testid={`delete-devis-${devis.devis_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Payeurs */}
      <div className="mb-3 space-y-2">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Payeur(s) :</p>
        {payeurs.length > 0 ? (
          <div className="space-y-2">
            {payeurs.map((p, pIdx) => (
              <PayeurCard
                key={pIdx}
                payeur={p}
                devis={devis}
                hasMultiplePayeurs={hasMultiplePayeurs}
                selectedProspect={selectedProspect}
                onPreviewDevis={onPreviewDevis}
                onDownloadDevis={onDownloadDevis}
                onSendDevisByEmail={onSendDevisByEmail}
                onCreateFacture={onCreateFacture}
                onPreviewFacture={onPreviewFacture}
                onDownloadFacture={onDownloadFacture}
                onSendFactureByEmail={onSendFactureByEmail}
                onPaiement={(payeur) => onPaiement(devis, payeur)}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-between p-2 rounded border bg-slate-100 text-slate-600">
            <span className="text-sm">Total</span>
            <span className="font-bold">{(devis.montant_total || 0).toLocaleString('fr-FR')} €</span>
          </div>
        )}
      </div>

      {/* BC sélectionnés */}
      {devis.bc_codes?.length > 0 && (
        <div className="mb-2 text-xs text-slate-500">
          <span className="font-medium">Blocs :</span> {devis.bc_codes.join(', ')}
        </div>
      )}
      
      {/* Signature */}
      {devis.statut === 'accepte' && devis.signature && (
        <div className="mt-2 pt-2 border-t border-green-200">
          <p className="text-xs text-green-600 font-medium">✓ Signé par {devis.accepte_par} le {new Date(devis.date_acceptation).toLocaleDateString('fr-FR')}</p>
        </div>
      )}
      
      {/* Facture liée */}
      {devis.facture_id && (
        <div className="mt-2 pt-2 border-t border-green-200">
          <p className="text-xs text-blue-600 font-medium flex items-center gap-1">
            <Receipt className="h-3 w-3" />
            Facture générée: #{devis.facture_id}
          </p>
        </div>
      )}
      
      {/* Actions globales */}
      <div className="mt-3 pt-2 border-t border-green-200 flex flex-wrap gap-2">
        <Button 
          size="sm" 
          variant="outline" 
          className="h-7 text-xs"
          onClick={() => onDownloadAllDevis(devis.devis_id, hasMultiplePayeurs && !partage)}
          data-testid={`download-devis-${devis.devis_id}`}
          title={hasMultiplePayeurs && !partage ? "Télécharger tous les devis (ZIP)" : "Télécharger le devis"}
        >
          <Download className="h-3 w-3 mr-1" />
          {hasMultiplePayeurs && !partage ? "Télécharger tout (ZIP)" : "Télécharger"}
        </Button>
        {partage && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onPreviewDevis(devis.devis_id)} data-testid={`preview-devis-${devis.devis_id}`}>
              <Eye className="h-3 w-3 mr-1" />
              Voir
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSendDevisByEmail(devis.devis_id)} data-testid={`send-devis-${devis.devis_id}`}>
              <Send className="h-3 w-3 mr-1" />
              Envoyer devis
            </Button>
          </>
        )}
        
        {devis.statut !== 'accepte' && devis.statut !== 'refuse' && (
          <Button 
            size="sm" 
            variant="outline" 
            className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => onRefuseDevis(devis.devis_id)}
            data-testid={`refuse-devis-${devis.devis_id}`}
          >
            <XCircle className="h-3 w-3 mr-1" />
            Refuser
          </Button>
        )}
        
        {(devis.statut === 'accepte' || devis.statut === 'signe') && (
          <Button 
            size="sm" 
            variant="outline"
            className="h-7 text-xs border-green-300 text-green-600 hover:bg-green-50"
            onClick={() => onPaiementDevis(devis)}
            data-testid={`payment-devis-${devis.devis_id}`}
          >
            <DollarSign className="h-3 w-3 mr-1" />
            Paiement
          </Button>
        )}
      </div>
    </div>
  );
};

// Composant pour afficher une facture
const FactureCard = ({ 
  facture, 
  onPreview, 
  onDownload, 
  onSendEmail, 
  onPaiement, 
  onEcheancier,
  onEditPaiement,
  onDeletePaiement
}) => (
  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-xs bg-blue-100 px-2 py-1 rounded">{facture.facture_numero}</span>
      <Badge className={
        facture.statut === 'payee' ? 'bg-green-500' :
        facture.statut === 'partielle' ? 'bg-orange-500' :
        facture.statut === 'en_retard' ? 'bg-red-500' : 'bg-blue-500'
      }>
        {facture.statut === 'payee' ? 'Payée' :
         facture.statut === 'partielle' ? 'Partielle' :
         facture.statut === 'en_retard' ? 'En retard' : 'Émise'}
      </Badge>
    </div>
    <div className="flex justify-between items-center">
      <div className="text-sm text-slate-600">
        <p>{new Date(facture.date_creation).toLocaleDateString('fr-FR')}</p>
        {(facture.total_paye || 0) > 0 && (
          <p className="text-xs text-green-600">Payé: {(facture.total_paye || 0).toLocaleString('fr-FR')} €</p>
        )}
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-blue-700">{(facture.montant_total || 0).toLocaleString('fr-FR')} €</p>
        {facture.statut !== 'payee' && (
          <p className="text-xs text-orange-600">Reste: {(facture.solde_restant || facture.montant_total || 0).toLocaleString('fr-FR')} €</p>
        )}
      </div>
    </div>
    
    {/* Échéancier */}
    {facture.echeancier?.length > 0 && (
      <div className="mt-2 pt-2 border-t border-blue-200">
        <p className="text-xs font-medium text-slate-500 mb-1">Échéancier:</p>
        <div className="space-y-1">
          {facture.echeancier.map((ech, i) => (
            <div key={i} className={`flex justify-between text-xs px-2 py-1 rounded ${
              ech.statut === 'paye' ? 'bg-green-100' : 
              ech.statut === 'en_retard' ? 'bg-red-100' : 'bg-slate-100'
            }`}>
              <span>{new Date(ech.date_echeance).toLocaleDateString('fr-FR')}</span>
              <span className="font-medium">{ech.montant.toLocaleString('fr-FR')} €</span>
              <span className={ech.statut === 'paye' ? 'text-green-600' : ech.statut === 'en_retard' ? 'text-red-600' : ''}>
                {ech.statut === 'paye' ? '✓ Payé' : ech.statut === 'en_retard' ? '⚠ Retard' : 'En attente'}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {/* Paiements enregistrés */}
    {facture.paiements?.length > 0 && (
      <div className="mt-2 pt-2 border-t border-blue-200">
        <p className="text-xs font-medium text-slate-500 mb-1">Paiements enregistrés:</p>
        <div className="space-y-1">
          {facture.paiements.map((pmt, i) => (
            <div key={pmt.paiement_id || i} className="flex items-center justify-between bg-white rounded-md px-3 py-1.5 text-xs border border-slate-100" data-testid={`facture-paiement-${pmt.paiement_id}`}>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 font-medium">{pmt.date_paiement ? new Date(pmt.date_paiement).toLocaleDateString('fr-FR') : '-'}</span>
                <Badge variant="outline" className="text-xs h-5">{pmt.mode_paiement || 'Virement'}</Badge>
                {pmt.reference && <span className="text-slate-400">Réf: {pmt.reference}</span>}
                {pmt.echeance_nom && <span className="text-purple-500">{pmt.echeance_nom}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-green-600">+{pmt.montant?.toLocaleString('fr-FR')} €</span>
                {onEditPaiement && (
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-blue-400 hover:text-blue-600" 
                    onClick={(e) => { e.stopPropagation(); onEditPaiement(pmt); }}
                    data-testid={`edit-paiement-${pmt.paiement_id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
                {onDeletePaiement && (
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); onDeletePaiement(pmt); }}
                    data-testid={`delete-paiement-${pmt.paiement_id}`}>
                    <XCircle className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="mt-2 pt-2 border-t border-blue-200 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onPreview}>
        <Eye className="h-3 w-3 mr-1" />
        Voir
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onDownload}>
        <Download className="h-3 w-3 mr-1" />
        PDF
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50" onClick={onSendEmail}>
        <Send className="h-3 w-3 mr-1" />
        Email
      </Button>
      {facture.statut !== 'payee' && (
        <>
          <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={onPaiement}>
            <CreditCard className="h-3 w-3 mr-1" />
            Paiement
          </Button>
          {!facture.echeancier?.length && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-purple-300 text-purple-600 hover:bg-purple-50" onClick={onEcheancier}>
              <CalendarClock className="h-3 w-3 mr-1" />
              Échéancier
            </Button>
          )}
        </>
      )}
    </div>
  </div>
);

export const DetailsDialog = ({
  open,
  onOpenChange,
  selectedProspect,
  getStatusBadge,
  // Callbacks pour devis
  onDeleteDevis,
  onPreviewDevis,
  onDownloadDevis,
  onSendDevisByEmail,
  onCreateFacture,
  onDownloadAllDevis,
  onRefuseDevis,
  onPaiementDevis,
  onPaiementPayeur,
  // Callbacks pour factures
  onPreviewFacture,
  onDownloadFacture,
  onSendFactureByEmail,
  onPaiementFacture,
  onEcheancierFacture,
  onEditPaiementFacture,
  onDeletePaiementFacture,
  // Actions rapides
  onSendEmail,
  onGenerateQuote,
  onEdit
}) => {
  const [emailVu, setEmailVu] = useState(null);
  if (!selectedProspect) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="details-dialog">
        <DialogHeader>
          <DialogTitle>{selectedProspect.prenom} {selectedProspect.nom}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2">
              {getStatusBadge(selectedProspect.statut)}
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700" data-testid="details-origine">
                Origine : {libelleOrigine(selectedProspect) || 'non renseignée'}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Contact & Adresse */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-slate-500 mb-2">Contact</h4>
              <div className="space-y-1">
                <p className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {selectedProspect.email || <span className="text-slate-400">Pas d'e-mail</span>}
                </p>
                {selectedProspect.telephone && (
                  <p className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-slate-400" />
                    {selectedProspect.telephone}
                  </p>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-500 mb-2">Adresse</h4>
              <p className="text-sm">
                {selectedProspect.adresse || '-'}<br />
                {selectedProspect.code_postal} {selectedProspect.ville}
              </p>
            </div>
          </div>
          
          {/* Formation */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Formation</h4>
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-sm"><strong>Diplôme:</strong> {selectedProspect.diplome_souhaite}</p>
              {selectedProspect.date_debut_souhaitee && (
                <p className="text-sm"><strong>Date souhaitée:</strong> {selectedProspect.date_debut_souhaitee}</p>
              )}
              {selectedProspect.mode_financement && (
                <p className="text-sm"><strong>Financement:</strong> {selectedProspect.mode_financement}</p>
              )}
              {selectedProspect.bc_selection?.length > 0 && (
                <p className="text-sm"><strong>BC sélectionnés:</strong> {selectedProspect.bc_selection.join(', ')}</p>
              )}
            </div>
          </div>
          
          {/* Notes */}
          {selectedProspect.notes && (
            <div>
              <h4 className="text-sm font-medium text-slate-500 mb-2">Notes</h4>
              <p className="text-sm p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                {selectedProspect.notes}
              </p>
            </div>
          )}
          
          {/* Historique des devis */}
          {selectedProspect.devis_historique?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-500 mb-2">Historique des devis ({selectedProspect.devis_historique.length})</h4>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {[...selectedProspect.devis_historique].reverse().map((devis, idx) => (
                  <DevisCard
                    key={idx}
                    devis={devis}
                    selectedProspect={selectedProspect}
                    onDeleteDevis={onDeleteDevis}
                    onPreviewDevis={onPreviewDevis}
                    onDownloadDevis={onDownloadDevis}
                    onSendDevisByEmail={onSendDevisByEmail}
                    onCreateFacture={onCreateFacture}
                    onPreviewFacture={onPreviewFacture}
                    onDownloadFacture={onDownloadFacture}
                    onSendFactureByEmail={onSendFactureByEmail}
                    onDownloadAllDevis={onDownloadAllDevis}
                    onRefuseDevis={onRefuseDevis}
                    onPaiement={onPaiementPayeur}
                    onPaiementDevis={onPaiementDevis}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Factures */}
          {selectedProspect.factures?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-500 mb-2">Factures</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {[...selectedProspect.factures].reverse().map((facture, idx) => (
                  <FactureCard
                    key={idx}
                    facture={facture}
                    onPreview={() => onPreviewFacture(facture.facture_id)}
                    onDownload={() => onDownloadFacture(facture.facture_id)}
                    onSendEmail={() => onSendFactureByEmail(facture.facture_id)}
                    onPaiement={() => onPaiementFacture(facture)}
                    onEcheancier={() => onEcheancierFacture(facture)}
                    onEditPaiement={onEditPaiementFacture ? (pmt) => onEditPaiementFacture(facture, pmt) : undefined}
                    onDeletePaiement={onDeletePaiementFacture ? (pmt) => onDeletePaiementFacture(facture, pmt) : undefined}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Suivi des relances */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
            <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Suivi des relances
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  (selectedProspect.nb_relances || 0) >= 3 ? 'text-red-600' :
                  (selectedProspect.nb_relances || 0) === 2 ? 'text-orange-600' :
                  (selectedProspect.nb_relances || 0) === 1 ? 'text-blue-600' : 'text-slate-400'
                }`}>
                  {selectedProspect.nb_relances || 0}
                </div>
                <div className="text-xs text-slate-500">Relance(s)</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-slate-700">
                  {selectedProspect.derniere_relance 
                    ? new Date(selectedProspect.derniere_relance).toLocaleDateString('fr-FR') : '-'}
                </div>
                <div className="text-xs text-slate-500">Dernière relance</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-slate-700">
                  {selectedProspect.derniere_relance_type === 'rappel_1' ? 'Mail 1' :
                   selectedProspect.derniere_relance_type === 'rappel_2' ? 'Mail 2' :
                   selectedProspect.derniere_relance_type === 'urgence' ? 'Mail 3 (Urgence)' :
                   selectedProspect.derniere_relance_type === 'custom' ? 'Personnalisé' :
                   selectedProspect.derniere_relance_type || '-'}
                </div>
                <div className="text-xs text-slate-500">Type de mail</div>
              </div>
            </div>
          </div>
          
          {/* Historique des contacts */}
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2">Historique des contacts</h4>
            {selectedProspect.historique_contact?.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {[...selectedProspect.historique_contact].reverse().map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      entry.type === 'email' ? 'bg-blue-100' : 
                      entry.type === 'devis' ? 'bg-green-100' : 
                      entry.type === 'paiement' ? 'bg-emerald-100' :
                      entry.type === 'changement_statut' ? 'bg-purple-100' : 'bg-slate-100'
                    }`}>
                      {entry.type === 'email' ? <Mail className="h-4 w-4 text-blue-600" /> :
                       entry.type === 'devis' ? <FileText className="h-4 w-4 text-green-600" /> :
                       entry.type === 'paiement' ? <CreditCard className="h-4 w-4 text-emerald-600" /> :
                       entry.type === 'changement_statut' ? <RefreshCw className="h-4 w-4 text-purple-600" /> :
                       <Clock className="h-4 w-4 text-slate-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{entry.notes}</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(entry.date).toLocaleString('fr-FR')}</p>
                      {entry.type === 'email' && (entry.email_id ? (
                        <button type="button" onClick={() => setEmailVu(entry.email_id)} className="mt-1 text-xs font-medium text-blue-600 hover:underline" data-testid={`historique-voir-mail-${idx}`}>
                          Voir le mail envoyé
                        </button>
                      ) : (
                        <p className="mt-1 text-xs italic text-slate-400">Contenu non conservé (envoi avant la mise à jour)</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 p-4 bg-slate-50 rounded-lg">Aucun contact enregistré</p>
            )}
          </div>
          
          {/* Actions rapides */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onSendEmail} data-testid="details-envoyer-email-btn">
              <Send className="h-4 w-4 mr-2" />
              Envoyer email
            </Button>
            <Button variant="outline" onClick={onGenerateQuote} data-testid="details-generer-devis-btn">
              <FileText className="h-4 w-4 mr-2" />
              Générer devis
            </Button>
            <Button variant="outline" onClick={onEdit} data-testid="details-modifier-btn">
              <Edit2 className="h-4 w-4 mr-2" />
              Modifier
            </Button>
          </div>
        </div>
        <EmailEnvoyeDialog emailId={emailVu} onClose={() => setEmailVu(null)} />
      </DialogContent>
    </Dialog>
  );
};

export default DetailsDialog;
